import React, { ReactNode } from 'react';
import { SectionBlockType } from '@appTypes/CategoryAppType';
import {
  AccordionBlock,
  AccordionListBlock,
  BilguideSearchBlock,
  BodyCtaAndBodyBannerBlock,
  CampaignBlock,
  CardCarouselBlock,
  CategoryListWithScrollBlock,
  ColumnBlock,
  CompareMembershipBlock,
  ConsumerGuidesBlock,
  ConsumerTestProductList,
  CountdownBlock,
  CustomBenefitListBlock,
  DocumentSnapshots,
  EmbeddedArticleBlock,
  FAQGlobalReferenceBlock,
  FeaturedBenefitBlock,
  FeaturedModelBlock,
  FeaturedVehicleTestResult,
  GoogleMapsWrapper,
  HeroBlock,
  IframeBlock,
  ImageLinksBlock,
  ImageTitleAndLinkList,
  InputWithImageLinkBlock,
  KeyStatisticsBlock,
  LatestArticlesBlock,
  LivestreamBlock,
  MainEntries,
  MembershipBenefitHighlightListBlock,
  MembershipBenefitListBlock,
  MembershipBenefitsBlock,
  ModularButtonBlock,
  MultiRowArticleBlock,
  NafLocationsMapBlock,
  NavigationLinksBlock,
  NewsBlock,
  NewsfeedBlock,
  OtherInformationBlock,
  PortraitsWithTextBlock,
  ProductCardListBlock,
  ProductsLoggedInActivationFormBlock,
  QRCodeBlock,
  QnaFeedBlock,
  ReferencedComponentBlock,
  Results,
  RouteCharts,
  ScrollableTextBlocksWithBackgroundImages,
  SecondaryEntriesBlock,
  ShortsCarouselBlock,
  Shortcuts,
  ShowcaseBlock,
  SingleNavigationCardBlock,
  StepByStepList,
  TabBlock,
  TextAndImageBlock,
  TextBlock,
  ThemeHeroBlock,
  ThreeColumnBlock,
  Timeline,
  TravelSuggestions,
  TrialMembershipNavigationCardContainerBlock,
  TrialMembershipWelcomeCardBlock,
  TwoArticleBlock,
  VerticalArticleBlock,
  VippsLoginBlock,
} from './blockComponents';

type BlockOfType<T extends SectionBlockType['type']> = Extract<SectionBlockType, { type: T }>;

export type BlockRegistry = {
  [T in SectionBlockType['type']]?: (block: BlockOfType<T>) => ReactNode;
};

export const blockRegistry: BlockRegistry = {
  news: (block) => <NewsBlock data={block.data} />,
  membershipBenefits: (block) => (
    <MembershipBenefitsBlock benefits={block.data} total={block.total} title={block.title} />
  ),
  campaignBlock: (block) => <CampaignBlock data={block.data} />,
  campaignGlobalReference: (block) => <CampaignBlock data={block.data} />,
  textAndImage: (block) => <TextAndImageBlock block={block} />,
  latestArticles: (block) => <LatestArticlesBlock block={block} />,
  hero: (block) => <HeroBlock block={block} />,
  categoryScrollList: (block) => <CategoryListWithScrollBlock block={block} />,
  threeColumn: (block) => <ThreeColumnBlock block={block} />,
  accordionBlock: (block) => <AccordionBlock block={block} />,
  accordionListBlock: (block) => <AccordionListBlock block={block} />,
  timeline: (block) => <Timeline block={block} />,
  countdownBlock: (block) => <CountdownBlock block={block} />,
  newsfeedBlock: (block) => <NewsfeedBlock block={block.data} />,
  questionsAndAnswersBlock: (block) => <QnaFeedBlock block={block} />,
  heroWithTitleIngressSmallImageAndCta: (block) => <ThemeHeroBlock block={block} />,
  scrollableTextBlocksWithBackgroundImages: (block) => (
    <ScrollableTextBlocksWithBackgroundImages key={block.key} block={block} />
  ),
  testshowcase: (block) => <ShowcaseBlock block={block} />,
  livestreamBlock: (block) => <LivestreamBlock key={block.key} block={block} />,
  youtubeShorts: (block) => <ShortsCarouselBlock key={block.key} block={block} />,
  textBlock: (block) => <TextBlock block={block} />,
  navigationLinks: (block) => <NavigationLinksBlock block={block} />,
  keyStatistics: (block) => <KeyStatisticsBlock block={block} />,
  otherInformationBlock: (block) => <OtherInformationBlock block={block} />,
  nafLocationsMap: (block) => <NafLocationsMapBlock block={block} />,
  productCardListBlock: (block) => <ProductCardListBlock block={block} />,
  routeBlock: (block) => (
    <GoogleMapsWrapper>
      <RouteCharts block={block} />
    </GoogleMapsWrapper>
  ),
  multiRowArticleBlock: (block) => <MultiRowArticleBlock block={block} />,
  imageLinks: (block) => <ImageLinksBlock block={block} />,
  portraitsWithText: (block) => <PortraitsWithTextBlock block={block} />,
  inputWithImageLinkBlock: (block) => <InputWithImageLinkBlock block={block} />,
  iframeBlock: (block) => <IframeBlock block={block} />,
  featuredBenefitCampaign: (block) => <FeaturedBenefitBlock block={block} />,
  testResultBlock: (block) => <Results block={block} />,
  featuredVehicleTestResult: (block) => <FeaturedVehicleTestResult block={block} />,
  columnBlock: (block) => <ColumnBlock block={block} />,
  simpleArticle: (block) => <VerticalArticleBlock block={block} />,
  entry: (block) => <SingleNavigationCardBlock size="large" block={block} />,
  imageTitleAndLinkList: (block) => <ImageTitleAndLinkList block={block} />,
  stepByStepList: (block) => <StepByStepList block={block} />,
  membershipBenefitListBlock: () => <MembershipBenefitListBlock />,
  documentSnapshots: (block) => <DocumentSnapshots block={block} />,
  tabsSection: (block) => <TabBlock block={block} />,
  tabsSectionMembershipBenefit: (block) => <TabBlock block={block} />,
  secondaryEntriesBlock: (block) => <SecondaryEntriesBlock block={block} />,
  bilguideSearchBlock: (block) => <BilguideSearchBlock block={block} />,
  featuredModelBlock: (block) => <FeaturedModelBlock block={block} />,
  travelSuggestionsBlock: () => <TravelSuggestions />,
  nafnoArticleEmbedded: (block) => <EmbeddedArticleBlock block={block} />,
  referencedComponent: (block) => <ReferencedComponentBlock value={block} />,
  twoArticleBlock: (block) => <TwoArticleBlock node={block} />,
  trialMembershipNavigationCardContainerBlock: (block) => <TrialMembershipNavigationCardContainerBlock block={block} />,
  bodyCtaAndBodyBannerBlock: (block) => <BodyCtaAndBodyBannerBlock block={block} />,
  cardCarouselBlock: (block) => <CardCarouselBlock block={block} />,
  mainEntries: (block) => <MainEntries node={block} />,
  shortcuts: (block) => <Shortcuts node={block} />,
  qrCodeReference: (block) => <QRCodeBlock node={block} />,
  vippsLogin: (block) => <VippsLoginBlock value={block} />,
  trialMembershipWelcomeCardBlock: (block) => <TrialMembershipWelcomeCardBlock queryParam={block.queryParam} />,
  productsLoggedInActivationForm: (block) => <ProductsLoggedInActivationFormBlock node={block} />,
  compareMembershipBlock: (block) => <CompareMembershipBlock block={block} />,
  consumerGuideBlock: () => <ConsumerGuidesBlock />,
  membershipBenefitHighlights: (block) => <MembershipBenefitHighlightListBlock block={block} />,
  modularButtons: (block) => <ModularButtonBlock block={block} />,
  consumerTestProductList: (block) => <ConsumerTestProductList value={block} />,
  faqGlobalReference: (block) => <FAQGlobalReferenceBlock block={block} />,
  customBenefitListBlock: (block) => <CustomBenefitListBlock block={block} />,
};
